import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

const GamifiedProgress = ({ userStats, onAchievementUnlock }) => {
  const [showCelebration, setShowCelebration] = useState(false);
  const [newAchievement, setNewAchievement] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('wellness');

  // Achievement definitions
  const achievements = {
    wellness: [
      {
        id: 'first_breath',
        title: 'First Breath 🌬️',
        description: 'Complete your first breathing exercise',
        requirement: 'Complete 1 breathing exercise',
        progress: userStats?.breathing_sessions || 0,
        target: 1,
        unlocked: (userStats?.breathing_sessions || 0) >= 1,
        rarity: 'common',
        xp: 50
      },
      {
        id: 'breath_master',
        title: 'Breath Master 🧘‍♀️',
        description: 'Complete 25 breathing sessions',
        requirement: 'Complete 25 breathing exercises',
        progress: userStats?.breathing_sessions || 0,
        target: 25,
        unlocked: (userStats?.breathing_sessions || 0) >= 25,
        rarity: 'rare',
        xp: 250
      },
      {
        id: 'journal_starter',
        title: 'Story Teller 📝',
        description: 'Write your first journal entry',
        requirement: 'Write 1 journal entry',
        progress: userStats?.journal_entries || 0,
        target: 1,
        unlocked: (userStats?.journal_entries || 0) >= 1,
        rarity: 'common',
        xp: 75
      },
      {
        id: 'weekly_warrior',
        title: 'Weekly Warrior ⚡',
        description: 'Maintain a 7-day wellness streak',
        requirement: '7-day streak',
        progress: userStats?.streak_days || 0,
        target: 7,
        unlocked: (userStats?.streak_days || 0) >= 7,
        rarity: 'uncommon',
        xp: 200
      },
      {
        id: 'mindful_explorer',
        title: 'Mindful Explorer 🌟',
        description: 'Try 10 different activities',
        requirement: 'Complete 10 different activities',
        progress: userStats?.unique_activities || 0,
        target: 10,
        unlocked: (userStats?.unique_activities || 0) >= 10,
        rarity: 'rare',
        xp: 300
      },
      {
        id: 'zen_master',
        title: 'Zen Master 🕉️',
        description: 'Reach 30-day streak',
        requirement: '30-day streak',
        progress: userStats?.streak_days || 0,
        target: 30,
        unlocked: (userStats?.streak_days || 0) >= 30,
        rarity: 'legendary',
        xp: 1000
      }
    ],
    social: [
      {
        id: 'helper',
        title: 'Helper 🤝',
        description: 'Send encouragement to another user',
        requirement: 'Send 1 encouraging message',
        progress: userStats?.messages_sent || 0,
        target: 1,
        unlocked: (userStats?.messages_sent || 0) >= 1,
        rarity: 'common',
        xp: 100
      },
      {
        id: 'community_champion',
        title: 'Community Champion 👑',
        description: 'Help 10 community members',
        requirement: 'Send 10 encouraging messages',
        progress: userStats?.messages_sent || 0,
        target: 10,
        unlocked: (userStats?.messages_sent || 0) >= 10,
        rarity: 'epic',
        xp: 500
      }
    ],
    creativity: [
      {
        id: 'artist',
        title: 'Digital Artist 🎨',
        description: 'Complete your first creative activity',
        requirement: 'Complete 1 creative activity',
        progress: userStats?.creative_activities || 0,
        target: 1,
        unlocked: (userStats?.creative_activities || 0) >= 1,
        rarity: 'common',
        xp: 75
      },
      {
        id: 'creative_genius',
        title: 'Creative Genius 💡',
        description: 'Complete 20 creative activities',
        requirement: 'Complete 20 creative activities',
        progress: userStats?.creative_activities || 0,
        target: 20,
        unlocked: (userStats?.creative_activities || 0) >= 20,
        rarity: 'legendary',
        xp: 800
      }
    ]
  };

  // Level system
  const totalXP = Object.values(achievements).flat()
    .filter(achievement => achievement.unlocked)
    .reduce((sum, achievement) => sum + achievement.xp, 0);
  
  const currentLevel = Math.floor(totalXP / 1000) + 1;
  const xpToNextLevel = 1000 - (totalXP % 1000);
  const levelProgress = ((totalXP % 1000) / 1000) * 100;

  // Rarity colors
  const rarityColors = {
    common: 'from-gray-400 to-gray-500',
    uncommon: 'from-green-400 to-green-500',
    rare: 'from-blue-400 to-blue-500',
    epic: 'from-purple-400 to-purple-500',
    legendary: 'from-yellow-400 to-orange-500'
  };

  const rarityBorders = {
    common: 'border-gray-300',
    uncommon: 'border-green-300',
    rare: 'border-blue-300',
    epic: 'border-purple-300',
    legendary: 'border-yellow-300'
  };

  // Check for new achievements
  useEffect(() => {
    const allAchievements = Object.values(achievements).flat();
    const recentlyUnlocked = allAchievements.find(achievement => 
      achievement.unlocked && !achievement.wasShown
    );
    
    if (recentlyUnlocked) {
      setNewAchievement(recentlyUnlocked);
      setShowCelebration(true);
      recentlyUnlocked.wasShown = true;
      
      if (onAchievementUnlock) {
        onAchievementUnlock(recentlyUnlocked);
      }
      
      setTimeout(() => {
        setShowCelebration(false);
        setNewAchievement(null);
      }, 5000);
    }
  }, [userStats]);

  const getAchievementsByCategory = (category) => {
    return achievements[category] || [];
  };

  const getProgressPercentage = (achievement) => {
    return Math.min((achievement.progress / achievement.target) * 100, 100);
  };

  return (
    <div className="space-y-6" data-testid="gamified-progress">
      {/* Level Display */}
      <Card className="glass-card p-6 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${rarityColors.epic} flex items-center justify-center text-white text-xl font-bold`}>
              {currentLevel}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Level {currentLevel} Wellness Warrior</h3>
              <p className="text-sm text-gray-600">{xpToNextLevel} XP to next level</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{totalXP} XP</span>
              <span>{currentLevel * 1000} XP</span>
            </div>
            <Progress value={levelProgress} className="h-3" />
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">{Object.values(achievements).flat().filter(a => a.unlocked).length}</div>
              <div className="text-gray-600">Achievements</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{userStats?.streak_days || 0}</div>
              <div className="text-gray-600">Day Streak</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-pink-600">{userStats?.total_activities || 0}</div>
              <div className="text-gray-600">Activities</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Achievement Celebration */}
      {showCelebration && newAchievement && (
        <Card className={`glass-card p-6 bg-gradient-to-r ${rarityColors[newAchievement.rarity]} text-white animate-bounce border-4 ${rarityBorders[newAchievement.rarity]}`}>
          <div className="text-center space-y-3">
            <div className="text-4xl">🎉</div>
            <h3 className="text-xl font-bold">Achievement Unlocked!</h3>
            <div className="text-lg">{newAchievement.title}</div>
            <p className="text-sm opacity-90">{newAchievement.description}</p>
            <div className="text-sm font-medium">+{newAchievement.xp} XP</div>
          </div>
        </Card>
      )}

      {/* Achievement Categories */}
      <div className="space-y-4">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {Object.keys(achievements).map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? "default" : "outline"}
              className={selectedCategory === category ? "btn-primary" : "btn-secondary"}
            >
              {category === 'wellness' && '🧘‍♀️'}
              {category === 'social' && '🤝'}
              {category === 'creativity' && '🎨'}
              <span className="ml-2 capitalize">{category}</span>
            </Button>
          ))}
        </div>

        {/* Achievement Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {getAchievementsByCategory(selectedCategory).map((achievement) => {
            const progressPercentage = getProgressPercentage(achievement);
            
            return (
              <Card 
                key={achievement.id} 
                className={`p-6 border-2 transition-all duration-300 ${
                  achievement.unlocked 
                    ? `${rarityBorders[achievement.rarity]} bg-gradient-to-br from-white to-gray-50 shadow-lg` 
                    : 'border-gray-200 bg-gray-50 opacity-75'
                }`}
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className={`font-semibold ${
                          achievement.unlocked ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {achievement.title}
                        </h4>
                        <Badge 
                          className={`text-xs bg-gradient-to-r ${rarityColors[achievement.rarity]} text-white`}
                        >
                          {achievement.rarity}
                        </Badge>
                      </div>
                      <p className={`text-sm ${
                        achievement.unlocked ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        {achievement.description}
                      </p>
                    </div>
                    
                    <div className={`text-2xl ${
                      achievement.unlocked ? '' : 'grayscale opacity-50'
                    }`}>
                      {achievement.unlocked ? '✅' : '🔒'}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className={achievement.unlocked ? 'text-gray-600' : 'text-gray-400'}>
                        Progress: {achievement.progress}/{achievement.target}
                      </span>
                      {achievement.unlocked && (
                        <span className="text-indigo-600 font-medium">
                          +{achievement.xp} XP
                        </span>
                      )}
                    </div>
                    
                    <Progress 
                      value={progressPercentage} 
                      className={`h-2 ${
                        achievement.unlocked ? '' : 'opacity-50'
                      }`}
                    />
                  </div>
                  
                  <p className={`text-xs ${
                    achievement.unlocked ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {achievement.requirement}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Quick Stats */}
      <Card className="glass-card p-4">
        <h4 className="font-semibold text-gray-900 mb-3">Your Wellness Journey 📈</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{userStats?.breathing_sessions || 0}</div>
            <div className="text-gray-600">Breathing Sessions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-green-600">{userStats?.journal_entries || 0}</div>
            <div className="text-gray-600">Journal Entries</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-600">{userStats?.creative_activities || 0}</div>
            <div className="text-gray-600">Creative Works</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-orange-600">{userStats?.messages_sent || 0}</div>
            <div className="text-gray-600">Encouragements Sent</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GamifiedProgress;
